import { useEffect, useState, type ReactNode } from "react";

const STORAGE_KEY_SESSION = "vb_age_confirmed";
const STORAGE_KEY_TIMESTAMP = "vb_age_confirmed_at";

interface AgeGateProps {
  children: ReactNode;
}

/**
 * AgeGate — full-screen 18+ confirmation modal that wraps protected
 * play/casino routes. Shows on first visit per browser session and
 * persists confirmation in sessionStorage. Children render only after
 * all three confirmation checkboxes are ticked and "Enter demo" is
 * clicked.
 *
 * Compliance:
 *  - 18+ age gate (21+ where required by jurisdiction)
 *  - Play-money disclaimer
 *  - Restricted-jurisdiction self-attestation (US, UK, FR, NL, AU, SG, IL)
 *  - "Leave" link points to GamCare.org.uk
 */
export function AgeGate({ children }: AgeGateProps) {
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [hydrated, setHydrated] = useState<boolean>(false);
  const [ageOk, setAgeOk] = useState<boolean>(false);
  const [demoOk, setDemoOk] = useState<boolean>(false);
  const [jurisdictionOk, setJurisdictionOk] = useState<boolean>(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY_SESSION) === "1") {
        setConfirmed(true);
      }
    } catch {
      // sessionStorage may be blocked (private mode, etc.) — re-prompt
    }
    setHydrated(true);
  }, []);

  const allChecked = ageOk && demoOk && jurisdictionOk;

  const handleEnter = () => {
    if (!allChecked) return;
    try {
      sessionStorage.setItem(STORAGE_KEY_SESSION, "1");
      localStorage.setItem(STORAGE_KEY_TIMESTAMP, new Date().toISOString());
    } catch {
      // best-effort persistence
    }
    setConfirmed(true);
  };

  if (!hydrated) {
    // Avoid flash of children before sessionStorage check completes
    return null;
  }

  if (confirmed) {
    return <>{children}</>;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.92)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        style={{
          background: "var(--bg-2, #0e1116)",
          color: "var(--fg-1, #e8ecf1)",
          border: "1px solid var(--line, #1f2630)",
          borderRadius: 12,
          maxWidth: 520,
          width: "100%",
          padding: "2rem 1.75rem",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          fontFamily: "var(--font-display, 'Inter', system-ui, sans-serif)",
        }}
      >
        <h2
          id="age-gate-title"
          style={{
            fontSize: "1.5rem",
            fontWeight: 600,
            margin: "0 0 0.75rem",
            color: "var(--gold-bright, #f5c542)",
            letterSpacing: "0.01em",
          }}
        >
          You must be 18+ to access the demo.
        </h2>
        <p
          style={{
            fontSize: "0.9rem",
            lineHeight: 1.55,
            opacity: 0.82,
            margin: "0 0 1.25rem",
          }}
        >
          Please confirm the following before entering the play-money demo.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1.5rem" }}>
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={ageOk}
              onChange={(e) => setAgeOk(e.target.checked)}
              style={checkboxStyle}
            />
            <span>I am at least 18 years of age (21+ if required by my jurisdiction)</span>
          </label>

          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={demoOk}
              onChange={(e) => setDemoOk(e.target.checked)}
              style={checkboxStyle}
            />
            <span>I understand this is a play-money demo with no real-money winnings</span>
          </label>

          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={jurisdictionOk}
              onChange={(e) => setJurisdictionOk(e.target.checked)}
              style={checkboxStyle}
            />
            <span>
              I am not located in a restricted jurisdiction (US, UK, France, Netherlands,
              Australia, Singapore, Israel)
            </span>
          </label>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
          <a
            href="https://www.gamcare.org.uk"
            style={{
              color: "var(--fg-1, #e8ecf1)",
              opacity: 0.6,
              fontSize: "0.85rem",
              textDecoration: "underline",
            }}
          >
            Leave
          </a>
          <button
            type="button"
            onClick={handleEnter}
            disabled={!allChecked}
            style={{
              background: allChecked ? "var(--gold-bright, #f5c542)" : "var(--line, #1f2630)",
              color: allChecked ? "#000" : "var(--fg-1, #e8ecf1)",
              border: "1px solid var(--line, #1f2630)",
              borderRadius: 8,
              padding: "0.65rem 1.25rem",
              fontSize: "0.95rem",
              fontWeight: 600,
              cursor: allChecked ? "pointer" : "not-allowed",
              opacity: allChecked ? 1 : 0.55,
              transition: "opacity 120ms ease, background 120ms ease",
              fontFamily: "inherit",
            }}
          >
            Enter demo
          </button>
        </div>
      </div>
    </div>
  );
}

const checkboxRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "0.65rem",
  fontSize: "0.875rem",
  lineHeight: 1.5,
  cursor: "pointer",
  userSelect: "none",
};

const checkboxStyle: React.CSSProperties = {
  marginTop: "0.2rem",
  width: 16,
  height: 16,
  accentColor: "var(--gold-bright, #f5c542)",
  cursor: "pointer",
  flexShrink: 0,
};
